import { Component, ContentType, Field } from 'contensis-core-api';
import { TagGroup } from 'contensis-management-api';
import { ContentTypeFieldHelper } from './ContentTypeFieldHelper';
import { ContentTypeDependencyResolver } from './ContentTypeDependencyResolver';
import { FormContentType } from '~/models/FormContentTypeManagement';

/**
 * A series of static helper methods for resolving content types or components
 * Instance-based implementations
 */
export class ContentTypeResolver {
  static contentType = (caseInsensitiveId = '', contentTypes: ContentType[]) =>
    contentTypes.find(
      (c) => c.id.toLowerCase() === caseInsensitiveId.toLowerCase()
    );

  static contentTypes = (
    caseInsensitiveIds: string[] = [],
    contentTypes: ContentType[]
  ) => {
    const ids = caseInsensitiveIds.map((caseInsensitiveId) =>
      caseInsensitiveId.toLowerCase()
    );
    return contentTypes.filter((c) => ids.includes(c.id.toLowerCase()));
  };

  static component = (caseInsensitiveId = '', components: Component[]) => {
    const id = caseInsensitiveId.startsWith('component.')
      ? caseInsensitiveId.replace('component.', '')
      : caseInsensitiveId;

    return components.find((c) => c.id.toLowerCase() === id.toLowerCase());
  };

  static components = (
    caseInsensitiveIds: string[] = [],
    components: Component[]
  ) => {
    const ids = caseInsensitiveIds.map((caseInsensitiveId) =>
      caseInsensitiveId.startsWith('component.')
        ? caseInsensitiveId.replace('component.', '').toLowerCase()
        : caseInsensitiveId.toLowerCase()
    );

    return components.filter((c) => ids.includes(c.id.toLowerCase()));
  };

  static tagGroup = (caseInsensitiveId = '', tagGroups: TagGroup[]) => {
    const id = caseInsensitiveId.startsWith('tag.')
      ? caseInsensitiveId.replace('tag.', '')
      : caseInsensitiveId;

    return tagGroups.find((g) => g.id.toLowerCase() === id.toLowerCase());
  };

  static tagGroups = (
    caseInsensitiveIds: string[] = [],
    tagGroups: TagGroup[]
  ) => {
    const ids = caseInsensitiveIds.map((caseInsensitiveId) =>
      caseInsensitiveId.startsWith('tag.')
        ? caseInsensitiveId.replace('tag.', '').toLowerCase()
        : caseInsensitiveId.toLowerCase()
    );

    return tagGroups.filter((g) => ids.includes(g.id.toLowerCase()));
  };

  static resource = (
    caseInsensitiveId: string,
    contentTypes: ContentType[],
    components: Component[]
  ) => {
    const isContentType = ContentTypeResolver.contentType(
      caseInsensitiveId,
      contentTypes
    );
    const isComponent = ContentTypeResolver.component(
      caseInsensitiveId,
      components
    );
    return isContentType || isComponent;
  };

  private dependencyResolver: ContentTypeDependencyResolver;

  constructor(
    private _contentTypes: ContentType[],
    private _components: Component[],
    private _tagGroups: TagGroup[]
  ) {
    this.dependencyResolver = new ContentTypeDependencyResolver(this);
  }

  contentType = (
    caseInsensitiveId: string | undefined,
    contentTypes = this._contentTypes
  ) => ContentTypeResolver.contentType(caseInsensitiveId, contentTypes);

  contentTypeId = (
    caseInsensitiveId: string,
    contentTypes = this._contentTypes
  ) => {
    if (caseInsensitiveId) {
      const found = this.contentType(caseInsensitiveId, contentTypes);
      if (found?.id) return found.id;
    }
    return `${caseInsensitiveId} - not found`;
  };

  contentTypes = (
    caseInsensitiveIds: string[] | undefined,
    contentTypes = this._contentTypes
  ) => ContentTypeResolver.contentTypes(caseInsensitiveIds, contentTypes);

  component = (
    caseInsensitiveId: string | undefined,
    components = this._components
  ) => ContentTypeResolver.component(caseInsensitiveId, components);

  componentId = (
    caseInsensitiveId: string | undefined,
    components = this._components
  ) => {
    if (caseInsensitiveId) {
      const found = this.component(caseInsensitiveId, components);
      if (found?.id) return found.id;
    }
    return `${caseInsensitiveId} - not found`;
  };

  components = (
    caseInsensitiveIds: string[] | undefined,
    components = this._components
  ) => ContentTypeResolver.components(caseInsensitiveIds, components);

  tagGroup = (
    caseInsensitiveId: string | undefined,
    tagGroups = this._tagGroups
  ) => ContentTypeResolver.tagGroup(caseInsensitiveId, tagGroups);

  tagGroupId = (
    caseInsensitiveId: string | undefined,
    tagGroups = this._tagGroups
  ) => {
    if (caseInsensitiveId) {
      const found = this.tagGroup(caseInsensitiveId, tagGroups);
      if (found?.id) return found.id;
    }
    return `${caseInsensitiveId} - not found`;
  };

  tagGroups = (
    caseInsensitiveIds: string[] | undefined,
    tagGroups = this._tagGroups
  ) => ContentTypeResolver.tagGroups(caseInsensitiveIds, tagGroups);

  resource = (
    caseInsensitiveId: string | undefined,
    contentTypes = this._contentTypes,
    components = this._components
  ) =>
    caseInsensitiveId
      ? ContentTypeResolver.resource(
          caseInsensitiveId,
          contentTypes,
          components
        )
      : undefined;

  /**
   * Loop through content type or component fields one time to look for all possible
   * dependency types in each field
   * @param resource Content type or Component
   * @returns list of dependency ids under each key of { contentTypeIds, componentIds, defaults, nodes }
   */
  dependencies = (resource: ContentType | Component) =>
    this.dependencyResolver.resolveIds(resource);

  /**
   * Resolve a child field within the supplied contentTypeId, for example field within a nested component.
   * Returns an object containing a field, any parent fields, and the level it
   * was resolved at.
   * @param fieldId can contain dot notation to resolve a field at any component depth starting at the uppermost field content type
   * @param contentTypeOrId the content type to resolve the field in
   * @param contentTypes optional: resolve the field within a specific list of contentTypes
   * @param components optional: resolve the field within a specific list of components
   * @returns
   */
  deepField = (
    fieldId: string,
    contentTypeOrId: ContentType | string,
    contentTypes = this._contentTypes,
    components = this._components
  ): {
    field?: Field;
    level?: number;
    parents?: Field[];
  } => {
    const contentType =
      typeof contentTypeOrId === 'string'
        ? this.resource(contentTypeOrId, contentTypes, components)
        : contentTypeOrId;
    const fieldHelper = ContentTypeFieldHelper;

    const fieldParts = fieldId.replaceAll('[]', '').split('.'); // fieldId could be `contentType.component.nested` or `category[]`
    const contentTypeFieldId = fieldParts[0]; // Content type
    const componentFieldId = fieldParts?.[1]; // Component
    const nestedComponentFieldId = fieldParts?.[2]; // Nested component

    const contentTypeField = contentType?.fields?.find(
      (f) => f.id.toLowerCase() === contentTypeFieldId.toLowerCase()
    );
    if (!componentFieldId)
      return { field: contentTypeField, level: 0, parents: [] };

    // Keep looking for component
    if (contentTypeField && fieldHelper.assert.IsComponent(contentTypeField)) {
      const componentId = fieldHelper.GetComponentId(contentTypeField);

      const component = this.component(componentId);

      const componentField = component?.fields?.find(
        (f) => f.id.toLowerCase() === componentFieldId.toLowerCase()
      );
      if (!nestedComponentFieldId)
        return {
          field: componentField,
          level: 1,
          parents: [contentTypeField],
        };

      // Keep looking for nested component
      if (componentField && fieldHelper.assert.IsComponent(componentField)) {
        const nestedComponentId = fieldHelper.GetComponentId(componentField);
        const nestedComponent = this.component(nestedComponentId);
        const nestedComponentField = nestedComponent?.fields?.find(
          (f) => f.id.toLowerCase() === nestedComponentFieldId.toLowerCase()
        );
        return {
          field: nestedComponentField,
          level: 2,
          parents: [contentTypeField, componentField],
        };
      }
    }
    return { field: undefined };
  };

  language = (resource: ContentType | Component | FormContentType) => {
    return (
      ('defaultLanguage' in resource && resource.defaultLanguage) ||
      Object.keys(resource.name)[0] ||
      'en-GB'
    );
  };
}
